import { useDebounce } from 'use-debounce';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Button, Card, Chip, Container, Dialog, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { getModifiedFieldsOnly } from '../../utils/getModifiedFieldsOnly';
import { FormData, useSubjectForm } from './hooks/useSubjectForm';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';
import { createSubject, deleteSubject, getSubjects, GetSubjectsParams, reorderSubjects, updateSubject } from '../../api/services/subjects';
import { Subject } from '../../api/interfaces/Subject';
import { PinnacleDraggableTable } from '../../components/PinnacleDraggableTable/PinnacleDraggableTable';
import { LoadingMode } from '../../api/interfaces/Common';
import CreateSubjectDrawerContent from './partials/CreateSubjectDrawerContent';
import EditSubjectDrawerContent from './partials/EditSubjectDrawerContent';

const subjectsColumn = [
    {
        key: 'name',
        label: 'Name',
        sx: { minWidth: 250, maxWidth: 250 },
    },
    {
        key: 'description',
        label: 'Description',
        sx: { minWidth: 250, maxWidth: 250 },
        render: (row: Subject) => (
            <span style={{ display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                {row.description}
            </span>
        )
    },
    {
        key: 'code',
        label: 'Code',
        sx: { maxWidth: 100 },
    },
    {
        key: 'isTrial',
        label: 'Trial',
        sx: { maxWidth: 50 },
        skipTooltip: true,
        render: (row: Subject) => (
            <>
                {row.isTrial && (
                    <Chip
                        label="trial"
                        variant="filled"
                        color="error"
                        sx={{
                            backgroundColor: '#FA3D3D33',
                            color: '#FA3D3D',
                            fontSize: '12px'
                        }}
                    />
                )}
            </>
        )
    },
];

const SubjectsPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = {
        programId: '',
        name: '',
        description: '',
        code: '',
        isTrial: false
    };

    // Initial touch fields for error validation
    const initialTouchFields = {
        programId: false,
        name: false,
        description: false,
        code: false,
        isTrial: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid
    } = useSubjectForm({
        initialFormData,
    });

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [programs, setPrograms] = useState<Program[]>([]);
    const [subjects, setSubjects] = useState<Subject[]>([]);

    const [page, setPage] = useState(1);
    const [hasMoreSubjects, setHasMoreSubjects] = useState(true);

    // Search subject with debounce
    const [searchSubject, setSearchSubject] = useState<string>('');
    const [debouncedSearchSubject] = useDebounce(searchSubject, 300);

    // Loading mode to handle different loading states
    const [loadingMode, setLoadingMode] = useState<LoadingMode>('none');

    // State for managing reordering of subjects
    const [selectedProgramFilter, setSelectedProgramFilter] = useState<string>('');
    const [selectedProgramNameFilter, setSelectedProgramNameFilter] = useState<string>('');

    // State to keep track of the original order of subjects by program
    const [originalSubjectsByProgramOrder, setOriginalSubjectsByProgramOrder] = useState<Subject[]>([]);

    const [isReordering, setIsReordering] = useState<boolean>(false);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Set the selected subject id for edit state
    const [selectedSubjectId, setSelectedSubjectId] = useState<string>('');
    const [selectedSubjectTitle, setSelectedSubjectTitle] = useState<string>('');

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalFormData, setOriginalFormData] = useState(initialFormData);

    // Drawable states for create and edit
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Handle the opening and closing of toggle
    const toggleDrawer = (open: boolean, state: 'create' | 'edit' | string) => () => {
        if (open) {
            setCurrentDrawableState(state);
            setCurrentDrawableTitle(state === 'create' ? 'Add Subject Term' : 'Edit Subject Information');
        } else {
            if (state === 'edit') {
                resetForm();
            }
            setCurrentDrawableState('');
        }

        setIsDrawerOpen(open);
    };

    const fetchPrograms = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchSubjects = useCallback(async () => {
        try {
            const params: GetSubjectsParams = {}
            if (selectedProgramFilter) {
                params.programId = selectedProgramFilter;
            }
            const subjectsResponse = await getSubjects(params);
            setSubjects(subjectsResponse.data.data);
            setPage(1);
            setHasMoreSubjects(true);

        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [selectedProgramFilter]);

    const fetchMoreSubjects = useCallback(async () => {
        try {
            setLoadingMode('scrolling');
            const nextPage = page + 1;

            const params: GetSubjectsParams = {
                page: nextPage,
            };

            if (debouncedSearchSubject) {
                params.keyword = debouncedSearchSubject;
            }

            if (selectedProgramFilter) {
                params.programId = selectedProgramFilter;
            }

            const response = await getSubjects(params);

            const { data: newSubjects, page: currentPage, totalPages } = response.data;

            setSubjects(prev => [...prev, ...newSubjects]);
            setPage(currentPage);
            setHasMoreSubjects(currentPage < totalPages);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    }, [page, debouncedSearchSubject, selectedProgramFilter]);

    const fetchSearchResults = useCallback(async () => {
        try {
            setLoadingMode('searching');
            const params: GetSubjectsParams = { keyword: debouncedSearchSubject }
            if (selectedProgramFilter) {
                params.programId = selectedProgramFilter;
            }
            const response = await getSubjects(params);

            const { data: newSubjects, page: currentPage, totalPages } = response.data;

            setSubjects(newSubjects);
            setPage(currentPage);
            setHasMoreSubjects(currentPage < totalPages);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    }, [debouncedSearchSubject, selectedProgramFilter]);

    const handleFilterSubjectsByProgramChange = async (event: SelectChangeEvent<string>) => {
        try {
            setSearchSubject('');
            setSelectedProgramFilter(event.target.value);
            setSelectedProgramNameFilter(programs.find(program => program._id === event.target.value)?.name || '');
            setLoadingMode('filtering');
            const response = await getSubjects({ programId: event.target.value });
            const { data: filteredSubjectsByProgram } = response.data;
            setSubjects(filteredSubjectsByProgram);

            if (!isReordering) {
                setOriginalSubjectsByProgramOrder(filteredSubjectsByProgram);
            }

            setPage(1);
            // Allow more subjects if the search field is empty
            event.target.value === '' ? setHasMoreSubjects(true) : setHasMoreSubjects(false);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    };

    const handleStartReorder = async (newOrder: Subject[]) => {
        if (!isReordering) {
            setOriginalSubjectsByProgramOrder([...subjects]);
        }
        setSubjects(newOrder);
        setIsReordering(true);
    };

    const handleSubmitReorder = async () => {
        try {
            setLoadingMode('filtering');
            const reorderSubjectsResponse = await reorderSubjects({
                programId: selectedProgramFilter,
                subjects: subjects.map(subject => subject._id).filter((id): id is string => typeof id === 'string')
            });

            pushNotification({
                details: reorderSubjectsResponse.message,
                type: NotificationTypes.Success,
            });

            setIsReordering(false);
            setOriginalSubjectsByProgramOrder(subjects);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    };

    const handleCancelReorder = () => {
        if (selectedProgramFilter) {
            const filtered = originalSubjectsByProgramOrder.filter(subject => subject.programId === selectedProgramFilter);
            setSubjects(filtered);
        } else {
            setSubjects(subjects);
        }

        setIsReordering(false);
    };

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const createResponse = await createSubject(formData);
            setSubjects((prevSubjects) => [createResponse.data, ...prevSubjects]);
            pushNotification({
                details: createResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (selectedSubject: Subject) => {
        setSelectedSubjectId(selectedSubject._id);

        const subjectData: FormData = {
            programId: selectedSubject.programId,
            name: selectedSubject.name,
            code: selectedSubject.code,
            description: selectedSubject.description,
            isTrial: selectedSubject.isTrial || false,
        };

        setOriginalFormData(subjectData);
        resetForm(subjectData);
        toggleDrawer(true, 'edit')();
    };

    const modifiedFields = useMemo(
        () => getModifiedFieldsOnly(formData, originalFormData),
        [formData, originalFormData]
    );

    const isFormModified = Object.keys(modifiedFields).length > 0;

    const handleUpdateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const updateResponse = await updateSubject(selectedSubjectId, modifiedFields);
            setSubjects((prevSubjects) =>
                prevSubjects.map((subject) =>
                    subject._id === updateResponse.data._id ? updateResponse.data : subject
                )
            );
            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    // Open the dialog propmt to confirm
    const handleDelete = (subject: Subject) => {
        setSelectedSubjectId(subject._id);
        setSelectedSubjectTitle(subject.name);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteSubject(selectedSubjectId);
            setSubjects(prevSubjects => prevSubjects.filter(subject => subject._id !== selectedSubjectId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedSubjectTitle('');
            setSelectedSubjectId('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    // Debouncing mechanism for search subject
    useEffect(() => {
        const search = async () => {
            setLoadingMode('searching');
            try {
                const trimmed = debouncedSearchSubject.trim();
                if (trimmed !== '') {
                    await fetchSearchResults();
                } else {
                    await fetchSubjects();
                }
            } finally {
                setLoadingMode('none');
            }
        };

        search();
    }, [debouncedSearchSubject, fetchSearchResults, fetchSubjects]);

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }

                setCurrentAdminName(firstName);
                await fetchPrograms();
                await fetchSubjects();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            }
        };
        run();
    }, [fetchSubjects, fetchPrograms, navigate]);

    return (
        <main className="main-content">
            <Sidebar />
            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Subjects' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        {/* Left side: New + Search */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{ mr: 2 }}
                                disabled={isReordering}
                                onClick={toggleDrawer(true, 'create')}
                            >
                                New Subject
                            </Button>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <PinnacleSearchField
                                    id="searchSubjects"
                                    label="Search subjects here..."
                                    value={searchSubject}
                                    onChange={setSearchSubject}
                                    isLoading={loadingMode === 'searching'}
                                    disabled={isReordering}
                                />
                            </Box>
                        </Box>

                        {/* Right side: Save + Cancel */}
                        {isReordering && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleSubmitReorder}
                                >
                                    Save Changes
                                </Button>
                                <Button
                                    variant="outlined"
                                    className="btn-white"
                                    onClick={handleCancelReorder}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            p: 3,
                            backgroundColor: '#fff',
                            boxShadow: '0px 4px 12px 8px rgba(211, 211, 211, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                            <Typography variant="body1" fontWeight={600}>
                                List of subjects {selectedProgramNameFilter && `under ${selectedProgramNameFilter}`}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#818181' }}>
                                    view by
                                </Typography>
                                <FormControl>
                                    <Select
                                        value={selectedProgramFilter}
                                        onChange={handleFilterSubjectsByProgramChange}
                                        defaultValue="All Programs"
                                        disabled={(loadingMode === 'searching' || loadingMode === 'filtering' || isReordering)}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Select Program' }}
                                        sx={{
                                            minWidth: 250,
                                            color: '#818181',
                                            '.MuiSelect-icon': {
                                                color: '#818181',
                                            },
                                        }}
                                        MenuProps={{
                                            disableScrollLock: true,
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="">All Programs</MenuItem>
                                        {programs.map((program) => (
                                            <MenuItem key={program._id} value={program._id}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block',
                                                    }}
                                                >
                                                    {program.name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>


                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ opacity: (loadingMode === 'searching' || loadingMode === 'filtering') ? 0.4 : 1 }}>
                                <InfiniteScroll
                                    dataLength={subjects.length}
                                    next={fetchMoreSubjects}
                                    hasMore={hasMoreSubjects}
                                    loader={
                                        loadingMode === 'scrolling' && (
                                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        )
                                    }
                                    scrollableTarget="scrollableSubjects"
                                >
                                    <PinnacleDraggableTable
                                        rows={subjects}
                                        columns={subjectsColumn}
                                        headerColor='#26C6F9'
                                        isDragEnabled={(selectedProgramFilter && !searchSubject) ? true : false}
                                        onEdit={(row) => handleEdit(row)}
                                        onDelete={(row) => handleDelete(row)}
                                        onReorder={(newOrder) => handleStartReorder(newOrder)}
                                        setIsReordering={setIsReordering}
                                        isReordering={isReordering}
                                    />
                                </InfiniteScroll>
                            </Box>
                            {(loadingMode === 'searching' || loadingMode === 'filtering') && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                />
                            )}
                        </Box>
                    </Card>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={() => toggleDrawer(false, currentDrawableState)()}
            >
                {currentDrawableState === 'create' && (
                    <CreateSubjectDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditSubjectDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleUpdateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                        isFormModified={isFormModified}
                    />
                )}
            </PinnacleDrawer>

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Subject"
                    itemToDelete={selectedSubjectTitle}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default SubjectsPage;
